import type { KPIMetric } from './types';

// ═══ Aggregate KPIs ═══
export const offerKPIs: Record<string, KPIMetric> = {
  totalActiveOffers: { value: 8, previousValue: 6, percentChange: 33.3, trend: 'up' },
  totalRedemptions: { value: 1247, previousValue: 1089, percentChange: 14.5, trend: 'up' },
  totalDiscountGiven: { value: 4832, previousValue: 4200, percentChange: 15.0, trend: 'up' },
  avgOrderValueWithOffers: { value: 34.20, previousValue: 31.80, percentChange: 7.5, trend: 'up' },
};

// ═══ Per-Offer Metrics ═══
export interface OfferMetrics {
  totalRedemptions: number;
  totalDiscountGiven: number;
  avgOrderValue: number;
  avgOrderValueWithout: number;
  redemptionsByDay: { date: string; count: number }[];
  topItems: { name: string; count: number }[];
  redemptionsByOrderType: { type: string; count: number }[];
}

const last30Days = Array.from({ length: 30 }, (_, i) => {
  const d = new Date();
  d.setDate(d.getDate() - (29 - i));
  return d.toISOString().split('T')[0];
});

export const offerMetrics: Record<string, OfferMetrics> = {
  'offer-1': {
    totalRedemptions: 342,
    totalDiscountGiven: 1368,
    avgOrderValue: 38.50,
    avgOrderValueWithout: 29.20,
    redemptionsByDay: last30Days.map((date) => ({ date, count: Math.floor(Math.random() * 18) + 5 })),
    topItems: [
      { name: 'Classic Burger', count: 89 },
      { name: 'Margherita Pizza', count: 67 },
      { name: 'Caesar Salad', count: 54 },
      { name: 'Chicken Wings', count: 41 },
      { name: 'French Fries', count: 38 },
    ],
    redemptionsByOrderType: [
      { type: 'Dine-In', count: 156 },
      { type: 'Online Pickup', count: 98 },
      { type: 'Online Delivery', count: 64 },
      { type: 'Phone', count: 24 },
    ],
  },
  'offer-2': {
    totalRedemptions: 198,
    totalDiscountGiven: 990,
    avgOrderValue: 42.10,
    avgOrderValueWithout: 29.20,
    redemptionsByDay: last30Days.map((date) => ({ date, count: Math.floor(Math.random() * 12) + 2 })),
    topItems: [
      { name: 'Ribeye Steak', count: 45 },
      { name: 'Grilled Salmon', count: 38 },
      { name: 'Lobster Tail', count: 29 },
      { name: 'Truffle Fries', count: 22 },
      { name: 'Chocolate Lava Cake', count: 18 },
    ],
    redemptionsByOrderType: [
      { type: 'Dine-In', count: 112 },
      { type: 'Online Pickup', count: 48 },
      { type: 'Online Delivery', count: 28 },
      { type: 'Walk-In', count: 10 },
    ],
  },
  'offer-3': {
    totalRedemptions: 267,
    totalDiscountGiven: 534,
    avgOrderValue: 25.80,
    avgOrderValueWithout: 29.20,
    redemptionsByDay: last30Days.map((date) => ({ date, count: Math.floor(Math.random() * 14) + 3 })),
    topItems: [
      { name: 'Combo Meal', count: 102 },
      { name: 'Kids Meal', count: 67 },
      { name: 'Breakfast Platter', count: 44 },
      { name: 'Veggie Wrap', count: 31 },
      { name: 'Iced Tea', count: 23 },
    ],
    redemptionsByOrderType: [
      { type: 'Online Delivery', count: 134 },
      { type: 'Online Pickup', count: 78 },
      { type: 'Dine-In', count: 42 },
      { type: 'Kiosk', count: 13 },
    ],
  },
};

// ═══ Top Offers Ranking ═══
export const topOffers = [
  { offerId: 'offer-1', name: '10% Off Everything', type: 'percentage-off' as const, redemptions: 342, discountGiven: 1368, status: 'active' as const },
  { offerId: 'offer-3', name: 'Free Delivery Over $25', type: 'free-delivery' as const, redemptions: 267, discountGiven: 534, status: 'active' as const },
  { offerId: 'offer-2', name: '$5 Off Orders Over $30', type: 'flat-dollar-off' as const, redemptions: 198, discountGiven: 990, status: 'active' as const },
  { offerId: 'offer-4', name: 'BOGO Burgers', type: 'bogo' as const, redemptions: 156, discountGiven: 2184, status: 'active' as const },
  { offerId: 'offer-5', name: 'Free Dessert on $50+', type: 'free-item' as const, redemptions: 134, discountGiven: 938, status: 'active' as const },
  { offerId: 'offer-6', name: 'Weekday Lunch 15% Off', type: 'percentage-off' as const, redemptions: 89, discountGiven: 623, status: 'paused' as const },
  { offerId: 'offer-7', name: 'Happy Hour BOGO Drinks', type: 'bogo' as const, redemptions: 61, discountGiven: 366, status: 'expired' as const },
];

// ═══ Redemption Trend (30 days) ═══
export const redemptionTrend = last30Days.map((date) => ({
  date,
  count: Math.floor(Math.random() * 40) + 20,
  discountAmount: Math.floor(Math.random() * 300) + 100,
}));

// ═══ Discount by Type Breakdown ═══
export const discountByType = [
  { type: '% Off', amount: 1991, percentage: 33, color: 'var(--accent)' },
  { type: '$ Off', amount: 990, percentage: 16, color: 'var(--green)' },
  { type: 'BOGO', amount: 2550, percentage: 42, color: 'var(--orange)' },
  { type: 'Free Item', amount: 938, percentage: 8, color: '#6366F1' },
  { type: 'Free Delivery', amount: 534, percentage: 1, color: '#EC4899' },
];
